import { useStore } from 'app/context';
import { UserModel } from 'app/models/core/user.model';
import { Table } from 'app/components/table/table';
import { AggregrationType, ITableColumn, TableModel, TableRow } from 'app/components/table/table.model';
import { ColorInfoBox, PairedBars, QuoteTotals, YearLegend, isRelevantUser } from './UsageAnmeldequoteShared';
import { formatNum } from 'app/utils';
import { observer } from 'mobx-react';
import moment from 'moment';
import { useEffect, useState } from 'react';

interface VdQuoteRow {
    vd: string;
    activeCount: number;
    loginCount: number;
    prevLoginCount: number;
    quote: number;
    prevQuote: number;
}

const buildRows = (users: UserModel[], currLoginIds: number[], prevLoginIds: number[]): VdQuoteRow[] => {
    const currSet = new Set(currLoginIds);
    const prevSet = new Set(prevLoginIds);

    const byVd = new Map<string, VdQuoteRow>();
    users.forEach((u) => {
        if (!isRelevantUser(u)) {
            return;
        }
        const vd = u.firstVd;
        if (!vd) {
            return;
        }
        let row = byVd.get(vd);
        if (!row) {
            row = {
                vd,
                activeCount: 0,
                loginCount: 0,
                prevLoginCount: 0,
                quote: 0,
                prevQuote: 0,
            };
            byVd.set(vd, row);
        }
        row.activeCount++;
        if (currSet.has(u.bnrId)) {
            row.loginCount++;
        }
        if (prevSet.has(u.bnrId)) {
            row.prevLoginCount++;
        }
    });

    const rows = Array.from(byVd.values());
    rows.forEach((r) => {
        r.quote = r.activeCount > 0 ? Math.round((r.loginCount / r.activeCount) * 1000) / 10 : 0;
        r.prevQuote = r.activeCount > 0 ? Math.round((r.prevLoginCount / r.activeCount) * 1000) / 10 : 0;
    });
    return rows.sort((a, b) => a.vd.localeCompare(b.vd));
};

export const UsageAnmeldequoteVDYtd = observer(() => {
    const { adminLogStore, adminUserStore } = useStore();
    const [rows, setRows] = useState<VdQuoteRow[]>([]);
    const [loading, setLoading] = useState(true);

    const now = moment();
    const currYear = now.year();
    const prevYear = currYear - 1;

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            // Vorjahresquote über den gleichen Zeitraum (1.1. bis heute), damit YTD vergleichbar ist
            const [users, currLoginIds, prevLoginIds] = await Promise.all([
                adminUserStore.findAllActiveUsers(),
                adminLogStore.loadLoginBnrIds(`${currYear}-01-01`, moment().add(1, 'day').format('YYYY-MM-DD')),
                adminLogStore.loadLoginBnrIds(`${prevYear}-01-01`, moment().subtract(1, 'year').add(1, 'day').format('YYYY-MM-DD')),
            ]);
            setRows(buildRows(users, currLoginIds, prevLoginIds));
            setLoading(false);
        };
        load();
    }, [adminLogStore, adminUserStore, currYear, prevYear]);

    if (loading) {
        return <div>Wird geladen...</div>;
    }

    const tm = new TableModel<VdQuoteRow, string>();
    const cols: ITableColumn<VdQuoteRow, string>[] = [
        {
            label: 'VD',
            path: 'vd',
        },
        {
            label: 'Aktive Benutzer',
            path: 'activeCount',
            format: formatNum,
            agg: [{ aggType: AggregrationType.sum }],
        },
        {
            label: 'Anmeldequote YTD',
            path: 'quote',
            render: (row: TableRow<VdQuoteRow>) => (
                <PairedBars curr={row.data.quote} prev={row.data.prevQuote} max={100} formatValue={(v) => `${v}%`} />
            ),
        },
    ];
    tm.setCols(cols);
    tm.sortBy = 'vd';
    tm.sortAsc = true;
    tm.idProperty = 'vd';
    tm.setRowData(rows);

    return (
        <>
            <ColorInfoBox currYear={currYear} prevYear={prevYear} />
            <Table tm={tm} stickyFooter={true} />
            <YearLegend currYear={currYear} prevYear={prevYear} />
            <QuoteTotals rows={rows} currYear={currYear} prevYear={prevYear} />
        </>
    );
});
